"use client";
import type React from "react";
import { useMemo } from "react";
import { cn } from "@resume-studio/utils";

export type TileCardProps = {
  title: string;
  subtitle?: string;
  imageUrl?: string | null;
  imageSrcSet?: string;
  imageSizes?: string;
  imageWidth?: number;
  imageHeight?: number;
  loading?: boolean;
  actions?: React.ReactNode;
  onOpen?: () => void;
  backgroundSeed?: string | number;
  hideMeta?: boolean;
  onImageLoad?: () => void;
  onImageError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  className?: string;
  containerClassName?: string;
  sheetClassName?: string;
  imageClassName?: string;
  actionsWrapperClassName?: string;
};

const BOARD_PRESETS = [
  { tone: "#EECDBD", cut: "#FBF8F4" },
  { tone: "#E6D7C8", cut: "#FBF8F4" },
  { tone: "#D7E4D7", cut: "#FBF8F4" },
  { tone: "#D9E8F7", cut: "#FBF8F4" },
];

function boardStyle(index: number): React.CSSProperties {
  const { tone, cut } = BOARD_PRESETS[index % BOARD_PRESETS.length];
  return {
    backgroundImage: `linear-gradient(145deg, ${cut} 0%, ${cut} 42%, ${tone} 42%, ${tone} 100%), radial-gradient(60% 60% at 55% 55%, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.08) 35%, rgba(0,0,0,0.04) 60%, rgba(0,0,0,0) 76%)`,
    backgroundBlendMode: "normal, multiply",
  } as const;
}

export const TileCard = ({
  title,
  subtitle,
  imageUrl,
  imageSrcSet,
  imageSizes,
  imageWidth,
  imageHeight,
  loading,
  actions,
  onOpen,
  backgroundSeed,
  hideMeta,
  onImageLoad,
  onImageError,
  className,
  containerClassName,
  sheetClassName,
  imageClassName,
  actionsWrapperClassName,
}: TileCardProps) => {
  const presetIndex = useMemo(() => {
    if (backgroundSeed === undefined || backgroundSeed === null) return 0;
    const s = String(backgroundSeed)
      .split("")
      .reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return s % BOARD_PRESETS.length;
  }, [backgroundSeed]);

  return (
    <div
      className={cn(`group cursor-pointer rounded-xl bg-transparent p-1 transition-all`, className)}
      {...(onOpen
        ? {
            onClick: () => onOpen?.(),
            role: "button" as const,
            tabIndex: 0,
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === "Enter" || e.key === " ") {
                onOpen?.();
              }
            },
          }
        : {})}
    >
      <div
        className={`relative aspect-[794/1123] w-full overflow-hidden rounded-xl shadow-sm ${containerClassName ?? ""}`}
        style={boardStyle(presetIndex)}
      >
        {actions ? (
          <div
            className={`pointer-events-none absolute right-2 top-2 z-10 flex translate-y-1 gap-1 opacity-0 transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100 ${actionsWrapperClassName ?? ""}`}
          >
            {actions}
          </div>
        ) : null}
        <div
          className={`absolute left-1/2 top-1/2 size-[85%] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-sm bg-white shadow-[0_12px_24px_rgba(0,0,0,0.15)] ring-1 ring-black/5 transition-transform duration-300 ease-out will-change-transform group-hover:scale-[1.015] group-hover:shadow-[0_18px_32px_rgba(0,0,0,0.2)] ${sheetClassName ?? ""}`}
        >
          {loading ? (
            <div className="flex size-full flex-col gap-2 overflow-hidden bg-white p-3">
              {/* Header section */}
              <div className="animate-pulse space-y-1">
                <div className="mb-1.5 h-3.5 w-2/3 rounded bg-gray-200" />
                <div className="h-2 w-1/2 rounded bg-gray-200" />
                <div className="h-1.5 w-1/3 rounded bg-gray-200" />
              </div>

              {/* Divider */}
              <div className="h-px animate-pulse bg-gray-200" />

              {/* Summary section */}
              <div className="animate-pulse space-y-1.5">
                <div className="mb-0.5 h-2 w-1/4 rounded bg-gray-300" />
                <div className="h-1.5 w-full rounded bg-gray-200" />
                <div className="h-1.5 w-full rounded bg-gray-200" />
                <div className="h-1.5 w-4/5 rounded bg-gray-200" />
              </div>

              {/* Experience section */}
              <div className="animate-pulse space-y-1.5">
                <div className="mb-0.5 h-2 w-1/3 rounded bg-gray-300" />
                <div className="space-y-1">
                  <div className="h-2 w-3/5 rounded bg-gray-200" />
                  <div className="h-1.5 w-2/5 rounded bg-gray-200" />
                  <div className="h-1.5 w-full rounded bg-gray-200" />
                  <div className="h-1.5 w-5/6 rounded bg-gray-200" />
                </div>
              </div>

              {/* Education section */}
              <div className="animate-pulse space-y-1.5">
                <div className="mb-0.5 h-2 w-1/4 rounded bg-gray-300" />
                <div className="space-y-0.5">
                  <div className="h-2 w-3/5 rounded bg-gray-200" />
                  <div className="h-1.5 w-2/5 rounded bg-gray-200" />
                </div>
              </div>

              {/* Skills section */}
              <div className="animate-pulse space-y-1.5">
                <div className="mb-0.5 h-2 w-1/5 rounded bg-gray-300" />
                <div className="flex flex-wrap gap-1">
                  <div className="h-3 w-10 rounded-full bg-gray-200" />
                  <div className="h-3 w-14 rounded-full bg-gray-200" />
                  <div className="h-3 w-9 rounded-full bg-gray-200" />
                  <div className="h-3 w-12 rounded-full bg-gray-200" />
                  <div className="h-3 w-10 rounded-full bg-gray-200" />
                </div>
              </div>
            </div>
          ) : (
            <img
              src={imageUrl ?? undefined}
              srcSet={imageSrcSet}
              sizes={imageSizes}
              loading="lazy"
              alt={`${title} preview`}
              width={imageWidth}
              height={imageHeight}
              className={`size-full object-contain ${imageClassName ?? ""}`}
              onLoad={onImageLoad}
              onError={onImageError}
            />
          )}
        </div>
      </div>
      {hideMeta ? null : (
        <div className="mt-3 space-y-1">
          <div className="line-clamp-2 text-sm font-medium text-foreground">{title}</div>
          {subtitle ? <div className="text-xs text-muted-foreground">{subtitle}</div> : null}
        </div>
      )}
    </div>
  );
};
